import React, { useMemo } from 'react';
import type { MessengerMessage } from '@/api/client';
import { DirectMessageBubble } from '@/components/Messenger/direct/DirectMessageBubble';
import { DirectMessageSelectableRow } from '@/components/Messenger/direct/DirectMessageSelectableRow';
import { DirectChatDayDivider } from '@/components/Messenger/direct/DirectChatDayDivider';
import { DirectChatDaySection } from '@/components/Messenger/direct/DirectChatDaySection';
import {
  directMessageLocalDayKey,
  formatDirectChatDayDividerLabel,
  isDirectMessageDayToday,
} from '@/components/Messenger/direct/directChatDayLabel';
import { getDirectDmThreadRowBleed } from '@/components/Messenger/direct/directDmThreadRowLayout';
import { getDirectMessageLogicalKey } from '@/components/Messenger/direct/directMessageLogicalKey';
import { useMessengerMessageEnterRegistry } from '@/components/Messenger/shared/messages/useMessengerMessageEnterRegistry';
import { MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY } from '@/constants/messengerSearchHighlight';
import { AgentAssistantMessageRow } from '@/components/Messenger/agent/AgentAssistantMessageRow';
import { AgentSystemMessageRow } from '@/components/Messenger/agent/AgentSystemMessageRow';
import { AgentUserMessageRow } from '@/components/Messenger/agent/AgentUserMessageRow';
import type { AgentChatModelOption, AgentChatQuotaSnapshot, ChatModelId } from '@/components/Messenger/agent/agentChatSharedTypes';
import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import type { TeleportContextValue } from '@/hooks/useTeleportToTarget';
import { resolveTrackerMessageTimelineIso } from '@/components/Messenger/unified/trackerMessageTimelineIso';

export type UnifiedThreadMessageListProps = {
  variant: 'thread';
  messages: MessengerMessage[];
  currentUserId: string | null;
  animationContextId: string;
  animateNewMessages?: boolean;
  viewportUnreadAnchorId?: string | null;
  selectionMode?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (msg: MessengerMessage) => void;
  onToggleMessageLike?: (msg: MessengerMessage) => void;
  onRetry?: (optimisticId: string) => void;
  onReply?: (msg: MessengerMessage) => void;
  onForward?: (msg: MessengerMessage) => void;
  onDelete?: (msg: MessengerMessage) => void;
  onStartEdit?: (msg: MessengerMessage) => void;
  onPin?: (msg: MessengerMessage) => void;
  onUnpin?: (msg: MessengerMessage) => void;
  onJumpToMessage?: (messageId: string) => void;
  showIncomingAuthorName?: boolean | string | null | ((msg: MessengerMessage, index: number) => boolean | string | null);
  relaxDeleteAgeLimit?: boolean;
  threadVariant?: 'direct' | 'group';
  resolveThreadVariant?: (msg: MessengerMessage, index: number) => 'direct' | 'group';
  sidebarSearchHighlightWords?: readonly string[];
};

export type UnifiedAgentMessageListProps = {
  variant: 'agent';
  animationContextId: string;
  messages: TrackerChatMessage[];
  status: string;
  teleport: TeleportContextValue | null;
  historyLoading: boolean;
  streamBusy: boolean;
  agentQuota: AgentChatQuotaSnapshot | null;
  chatModelId: ChatModelId;
  chatModels: AgentChatModelOption[];
  onForwardMessage: (draft: MessengerMessage) => void;
  sendQuickMessage: (m: { text: string }) => void;
  onEditMessage: (messageId: string) => void;
  onRetryUserWithModel: (messageId: string, modelId: ChatModelId) => void;
  onRetrySend: () => void;
  isModelSendBlocked: (modelId: ChatModelId) => boolean;
};

export type UnifiedMessageListProps = UnifiedThreadMessageListProps | UnifiedAgentMessageListProps;

const noopToggleSelect = () => {};

export function UnifiedMessageList(props: UnifiedMessageListProps) {
  if (props.variant === 'thread') {
    return <UnifiedThreadMessageList {...props} />;
  }
  return <UnifiedAgentMessageList {...props} />;
}

function threadListPropsEqual(
  prev: UnifiedThreadMessageListProps,
  next: UnifiedThreadMessageListProps
): boolean {
  return (
    prev.messages === next.messages &&
    prev.currentUserId === next.currentUserId &&
    prev.animationContextId === next.animationContextId &&
    prev.animateNewMessages === next.animateNewMessages &&
    prev.viewportUnreadAnchorId === next.viewportUnreadAnchorId &&
    prev.selectionMode === next.selectionMode &&
    prev.selectedIds === next.selectedIds &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.onToggleMessageLike === next.onToggleMessageLike &&
    prev.onRetry === next.onRetry &&
    prev.onReply === next.onReply &&
    prev.onForward === next.onForward &&
    prev.onDelete === next.onDelete &&
    prev.onStartEdit === next.onStartEdit &&
    prev.onPin === next.onPin &&
    prev.onUnpin === next.onUnpin &&
    prev.onJumpToMessage === next.onJumpToMessage &&
    prev.showIncomingAuthorName === next.showIncomingAuthorName &&
    prev.relaxDeleteAgeLimit === next.relaxDeleteAgeLimit &&
    prev.threadVariant === next.threadVariant &&
    prev.resolveThreadVariant === next.resolveThreadVariant &&
    prev.sidebarSearchHighlightWords === next.sidebarSearchHighlightWords
  );
}

const UnifiedThreadMessageList = React.memo(function UnifiedThreadMessageList({
  messages,
  currentUserId,
  animationContextId,
  animateNewMessages = true,
  viewportUnreadAnchorId = null,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
  onToggleMessageLike,
  onRetry,
  onReply,
  onForward,
  onDelete,
  onStartEdit,
  onPin,
  onUnpin,
  onJumpToMessage,
  showIncomingAuthorName = false,
  relaxDeleteAgeLimit = false,
  threadVariant = 'direct',
  resolveThreadVariant,
  sidebarSearchHighlightWords = MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY,
}: UnifiedThreadMessageListProps) {
  const logicalKeys = useMemo(() => messages.map(getDirectMessageLogicalKey), [messages]);
  const enteringKeys = useMessengerMessageEnterRegistry(
    animationContextId,
    logicalKeys,
    animateNewMessages
  );

  const runs = useMemo(() => {
    type Run = { dayKey: string; startIndex: number; msgs: MessengerMessage[] };
    const out: Run[] = [];
    for (let i = 0; i < messages.length; i += 1) {
      const msg = messages[i]!;
      const dayKey = directMessageLocalDayKey(msg.created_at);
      const tail = out[out.length - 1];
      if (!tail || tail.dayKey !== dayKey) {
        out.push({ dayKey, startIndex: i, msgs: [msg] });
      } else {
        tail.msgs.push(msg);
      }
    }
    return out;
  }, [messages]);

  const resolveShowIncomingAuthorName =
    typeof showIncomingAuthorName === 'function'
      ? showIncomingAuthorName
      : () => showIncomingAuthorName;

  return (
    <>
      {runs.map((run) => (
        <DirectChatDaySection key={run.dayKey} dayKey={run.dayKey}>
          <DirectChatDayDivider
            label={formatDirectChatDayDividerLabel(run.msgs[0]!.created_at)}
            sticky={!isDirectMessageDayToday(run.msgs[0]!.created_at)}
          />
          {run.msgs.map((msg, j) => {
            const idx = run.startIndex + j;
            const isSystem = msg.message_kind === 'system';
            const isOwn = isSystem ? false : msg.sender_id === currentUserId;
            /** Первое сообщение после разделителя дня (j === 0) не склеиваем с концом прошлого дня — показываем имя. */
            const sameAsPrev =
              j > 0 &&
              idx > 0 &&
              !isSystem &&
              messages[idx - 1]?.message_kind !== 'system' &&
              messages[idx - 1]?.sender_id === msg.sender_id;
            const showUnreadDivider = viewportUnreadAnchorId === msg.id;
            const isFailedTail = isOwn && msg._client?.status === 'failed';
            const stackWithPrev = sameAsPrev && !showUnreadDivider && !isFailedTail;
            const rowBleed = getDirectDmThreadRowBleed(msg, stackWithPrev);
            const logicalKey = getDirectMessageLogicalKey(msg);
            const incomingAuthorName = stackWithPrev ? false : resolveShowIncomingAuthorName(msg, idx);
            const bubbleThreadVariant = resolveThreadVariant?.(msg, idx) ?? threadVariant;

            return (
              <DirectMessageSelectableRow
                key={logicalKey}
                msg={msg}
                isOwn={isOwn}
                selectionMode={selectionMode}
                isSelected={selectedIds?.has(msg.id) ?? false}
                onToggleSelect={onToggleSelect ?? noopToggleSelect}
                rowBleed={rowBleed}
                stackWithPrev={stackWithPrev}
                onToggleMessageLike={onToggleMessageLike}
              >
                <DirectMessageBubble
                  anchorId={msg.id}
                  msg={msg}
                  isOwn={isOwn}
                  showUnreadDivider={showUnreadDivider}
                  currentUserId={currentUserId}
                  onRetry={onRetry}
                  onReply={onReply}
                  onForward={onForward}
                  onDelete={onDelete}
                  onStartEdit={onStartEdit}
                  onPin={onPin}
                  onUnpin={onUnpin}
                  onJumpToMessage={onJumpToMessage}
                  selectionMode={selectionMode}
                  onToggleSelect={onToggleSelect}
                  onToggleMessageLike={onToggleMessageLike}
                  showIncomingAuthorName={incomingAuthorName}
                  relaxDeleteAgeLimit={relaxDeleteAgeLimit}
                  animateEnter={enteringKeys.has(logicalKey)}
                  threadVariant={bubbleThreadVariant}
                  sidebarSearchHighlightWords={sidebarSearchHighlightWords}
                />
              </DirectMessageSelectableRow>
            );
          })}
        </DirectChatDaySection>
      ))}
    </>
  );
}, threadListPropsEqual);

function UnifiedAgentMessageList({
  animationContextId,
  messages,
  status,
  teleport,
  historyLoading,
  streamBusy,
  agentQuota: _agentQuota,
  chatModelId,
  chatModels,
  onForwardMessage,
  sendQuickMessage,
  onEditMessage,
  onRetryUserWithModel,
  onRetrySend,
  isModelSendBlocked,
}: UnifiedAgentMessageListProps) {
  const arr = messages;

  const timeline = useMemo(() => {
    let prevIso = new Date().toISOString();
    return arr.map((m) => {
      const iso = resolveTrackerMessageTimelineIso(m, prevIso);
      prevIso = iso;
      return { m, iso };
    });
  }, [arr]);

  const runs = useMemo(() => {
    type Run = { dayKey: string; startIndex: number; items: { m: TrackerChatMessage; iso: string }[] };
    const out: Run[] = [];
    for (let i = 0; i < timeline.length; i += 1) {
      const item = timeline[i]!;
      const dayKey = directMessageLocalDayKey(item.iso);
      const tail = out[out.length - 1];
      if (!tail || tail.dayKey !== dayKey) {
        out.push({ dayKey, startIndex: i, items: [item] });
      } else {
        tail.items.push(item);
      }
    }
    return out;
  }, [timeline]);

  const agentEnterLogicalKeys = useMemo(() => arr.map((m) => m.id), [arr]);
  const enteringKeys = useMessengerMessageEnterRegistry(animationContextId, agentEnterLogicalKeys, true);

  return (
    <>
      {runs.map((run) => (
        <DirectChatDaySection key={run.dayKey} dayKey={run.dayKey}>
          <DirectChatDayDivider
            label={formatDirectChatDayDividerLabel(run.items[0]!.iso)}
            sticky={!isDirectMessageDayToday(run.items[0]!.iso)}
          />
          {run.items.map((item, j) => {
            const idx = run.startIndex + j;
            const message = item.m;
            const prevMsg = idx > 0 ? arr[idx - 1] : null;
            const isSameRoleAsPrev = Boolean(
              prevMsg &&
                prevMsg.role === message.role &&
                message.role !== 'system' &&
                prevMsg.role !== 'system'
            );
            if (message.role === 'system') {
              return (
                <AgentSystemMessageRow
                  key={message.id}
                  message={message}
                  timelineIso={item.iso}
                  animateEnter={enteringKeys.has(message.id)}
                />
              );
            }
            if (message.role === 'assistant') {
              return (
                <AgentAssistantMessageRow
                  key={message.id}
                  message={message}
                  timelineIso={item.iso}
                  sameAsPrev={isSameRoleAsPrev}
                  animateEnter={enteringKeys.has(message.id)}
                  status={status}
                  sendQuickMessage={sendQuickMessage}
                  onForward={onForwardMessage}
                  chatModels={chatModels}
                />
              );
            }
            if (message.role === 'user') {
              return (
                <AgentUserMessageRow
                  key={message.id}
                  message={message}
                  timelineIso={item.iso}
                  sameAsPrev={isSameRoleAsPrev}
                  animateEnter={enteringKeys.has(message.id)}
                  teleport={teleport}
                  menuDisabled={status !== 'ready' || historyLoading || streamBusy}
                  chatModels={chatModels}
                  onEditMessage={onEditMessage}
                  onRetryUserWithModel={onRetryUserWithModel}
                  onRetrySend={onRetrySend}
                  retrySendDisabled={
                    status !== 'ready' || historyLoading || streamBusy || isModelSendBlocked(chatModelId)
                  }
                  isModelSendBlocked={isModelSendBlocked}
                />
              );
            }
            return null;
          })}
        </DirectChatDaySection>
      ))}
    </>
  );
}
